import { Button, Popover, Stack, Typography, useTheme } from '@mui/material'
import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Route, Routes, useNavigate, useParams } from 'react-router-dom'
import { useFetchConversationsWithTwin } from 'hooks/use-conversation'
import { Messages } from 'components/Conversations'
import { twinsAtom } from 'state/twins'
import { useRecoilState, useRecoilValue } from 'recoil'
import { Twin, TwinContextSource, TwinConversation, TwinConversationGroup, TwinMessage } from 'api/model'
import { useFetchConversationMessages } from 'hooks/use-messages'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import MobileOverlay from 'components/MobileOverlay/MobileOverlay'
import { getColorForDarkMode } from 'utils'
import { twinsGetSharedTwin } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { useActiveOrganization } from 'hooks/use-organization'
import axios from 'axios'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import AddConversationPopover from 'components/JelledBots/AddConversationPopover'
import { COLORS } from 'app-constants'

const TwinProfileContainer = () => {
  const [activeOrganization] = useActiveOrganization()
  const { shortUrl = '' } = useParams()
  const navigate = useNavigate()
  const [twins, setTwins] = useRecoilState(twinsAtom)

  // when this is loaded, we want to see if we can fetch the twin that the shortUrl represents
  useEffect(() => {
    async function fetchData() {
      if (!shortUrl || !activeOrganization) {
        return
      }

      try {
        // if response.data exists, then we should navigate the user to the proper url
        const response = await twinsGetSharedTwin(activeOrganization, shortUrl)
        if (response.data) {
          const existingTwin = twins.find((t) => t.id === response.data.id)
          if (!existingTwin) {
            setTwins((prev) => [...prev, response.data])
          }
          navigate(`/twin-profile/${response.data.id}/`)
        }
      } catch (error: unknown) {
        if (axios.isAxiosError(error) && error.message.includes('403')) {
          navigate('/twin-not-found')
        }
      }
    }

    fetchData()
  }, [navigate, shortUrl, activeOrganization, setTwins, twins])

  return (
    <Routes>
      <Route path=":twinId/*" element={<TwinProfile />} />
    </Routes>
  )
}

const ConversationsList = ({
  conversationId,
  setConversationId,
  conversationsWithTwin,
}: {
  conversationId: string
  setConversationId: (conversationId: string) => void
  conversationsWithTwin: TwinConversationGroup[]
}) => {
  const flattenedConversations = useMemo(() => {
    return conversationsWithTwin.flatMap((tcg) => tcg.twin_conversations)
  }, [conversationsWithTwin])

  if (conversationsWithTwin.length === 0) {
    return (
      <Typography variant="pXS" color="#999999" mt={2}>
        No conversations yet
      </Typography>
    )
  }

  return (
    <Stack>
      {flattenedConversations.map((tc) => (
        <Stack key={tc.id} mb={2}>
          <Stack
            borderRadius="8px"
            onClick={() => {
              setConversationId(tc.id)
            }}
            sx={{
              cursor: 'pointer',
              py: '8px',
              px: '16px',
              backgroundColor: conversationId === tc.id ? COLORS.GRAY_8 : 'transparent',
              '&:hover': {
                backgroundColor: COLORS.GRAY_8,
                color: 'secondary.main',
              },
            }}>
            <Stack key={tc.id}>
              <Typography variant="pS" color={conversationId === tc.id ? 'secondary' : 'lightGrey'}>
                {tc.title}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      ))}
    </Stack>
  )
}

const ConversationsSidebar = ({
  conversationId,
  setConversationId,
  conversationsWithTwin,
  onConversationAdded,
  twinId,
}: {
  conversationId: string
  setConversationId: (conversationId: string) => void
  conversationsWithTwin: TwinConversationGroup[]
  onConversationAdded: (twin: Twin, twinConversation: TwinConversation, verboseOutput: string) => void
  twinId?: string
}) => {
  const theme = useTheme()
  const isMobile = useCheckMobileScreen()
  const navigate = useNavigate()

  // logic for the AddConversationPopover component
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const open = Boolean(anchorEl)
  const handleClose = () => {
    setAnchorEl(null)
  }
  const id = open ? 'simple-popover' : undefined

  return (
    <Stack
      sx={{
        px: 3,
        pt: 3,
        backgroundColor: COLORS.PURE_WHITE,
      }}>
      <Button
        startIcon={<KeyboardArrowLeftIcon />}
        onClick={() => {
          navigate('/my-twin/sharing?isTwinDirectory=true', { replace: true })
        }}
        sx={{
          color: getColorForDarkMode(theme),
          width: 'fit-content',
          marginBottom: isMobile ? '16px' : '32px',
        }}>
        Back to twins shared with me
      </Button>

      <Stack
        width="260px"
        height="82vh"
        sx={{
          overflowY: 'scroll',
        }}>
        <Button
          className="add-conversation-button"
          variant="contained"
          color="primary"
          onClick={(e: any) => {
            const buttonEl = document.querySelector('.add-conversation-button')
            if (buttonEl) {
              setAnchorEl(buttonEl as HTMLButtonElement)
            }
          }}
          startIcon={
            <EditOutlinedIcon
              sx={{
                height: '16px',
                width: '16px',
              }}
            />
          }>
          New conversation
        </Button>
        <ConversationsList
          conversationId={conversationId}
          setConversationId={setConversationId}
          conversationsWithTwin={conversationsWithTwin}
        />
      </Stack>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: isMobile ? 'bottom' : 'top',
          horizontal: isMobile ? 'center' : 'right',
        }}
        transformOrigin={{
          vertical: isMobile ? 'top' : 'top',
          horizontal: isMobile ? 'center' : 'left',
        }}
        sx={{
          ml: 1,
        }}>
        <AddConversationPopover
          twinId={twinId}
          setConversationId={setConversationId}
          onConversationAdded={onConversationAdded}
          onClose={handleClose}
        />
      </Popover>
    </Stack>
  )
}

const TwinProfile = () => {
  const isMobile = useCheckMobileScreen()

  const { twinId } = useParams()

  const twin = useRecoilValue(twinsAtom).find((t) => t.id === twinId)

  const [conversationId, setConversationId] = useState<string>('')

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [conversation, setConversation] = useState<TwinConversation | undefined>(undefined)

  // used for debugging purposes
  const [verboseOutput, setVerboseOutput] = useState<string>('')
  const [contextSources, setContextSources] = useState<TwinContextSource[]>([])

  const { conversationsWithTwin, setConversationsWithTwin } = useFetchConversationsWithTwin(twinId ?? '')

  // whenever the twin that we're referencing changes, we want to reset the conversation id
  useEffect(() => {
    // by default, we want to set the first conversation as selected
    if (conversationsWithTwin.length > 0) {
      setConversationId(conversationsWithTwin[0].twin_conversations[0].id)
      setConversation(conversationsWithTwin[0].twin_conversations[0])
    } else {
      setConversationId('')
      setConversation(undefined)
    }
  }, [twinId, conversationsWithTwin])

  const {
    conversationMessages,
    setConversationMessages,
    isLoading: isLoadingConversationMessages,
  } = useFetchConversationMessages(conversation?.twin_id ?? '', conversationId)

  // we need to update conversationMessages in recoil
  const hydrateConversationMessages = (twinMessage: TwinMessage) => {
    setConversationMessages((prev) => [...prev, twinMessage])
  }

  const hydrateConversationsWithTwin = (twin: Twin, twinConversation: TwinConversation) => {
    setConversationsWithTwin((prev) => {
      // add the new conversation to the top of the list
      return [{ twin, twin_conversations: [twinConversation] }, ...prev]
    })
  }

  // this is used when we want to update a specific twin message in recoil
  const hydrateConversationMessage = (twinMessage: TwinMessage) => {
    setConversationMessages((prev) => {
      const updatedConversationMessages = prev.map((cm) => {
        if (cm.id === twinMessage.id) {
          return twinMessage
        }
        return cm
      })
      return updatedConversationMessages
    })
  }

  const flattenedConversations = useMemo(() => {
    return conversationsWithTwin.flatMap((tcg) => tcg.twin_conversations)
  }, [conversationsWithTwin])

  useLayoutEffect(() => {
    // we don't want the default conversation to be the first one in the list on mobile
    if (!conversationId && !isMobile) {
      if (flattenedConversations[0]) {
        setConversation(flattenedConversations[0])
        setConversationId(flattenedConversations[0].id)
      }
    } else {
      const conversation = flattenedConversations.find((tc) => tc.id === conversationId)
      if (conversation) {
        setConversation(conversation)
      }
    }
  }, [conversationId, flattenedConversations, isMobile])

  const onConversationAdded = (twin: Twin, twinConversation: TwinConversation, verboseOutput: string) => {
    hydrateConversationsWithTwin(twin, twinConversation)
    // we want the latest conversation to be the one that is selected
    setConversationId(twinConversation.id)
    setVerboseOutput(verboseOutput)
  }

  if (!twin) {
    return (
      <Stack>
        <Typography>Oops! Looks like there's no twin found.</Typography>
      </Stack>
    )
  }

  if (isMobile) {
    if (conversationId) {
      return (
        <MobileOverlay
          closeButtonColor="#222222"
          onClose={() => {
            setConversationId('')
            setConversation(undefined)
          }}>
          <Messages
            contextSources={contextSources}
            setContextSources={setContextSources}
            verboseOutput={verboseOutput}
            setVerboseOutput={setVerboseOutput}
            conversation={conversation}
            conversationMessages={conversationMessages}
            isLoadingConversationMessages={isLoadingConversationMessages}
            setConversationId={setConversationId}
            hydrateConversationMessages={hydrateConversationMessages}
            hydrateConversationMessage={hydrateConversationMessage}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
          />
        </MobileOverlay>
      )
    } else {
      return (
        <>
          <ConversationsSidebar
            conversationId={conversationId}
            setConversationId={setConversationId}
            conversationsWithTwin={conversationsWithTwin}
            onConversationAdded={onConversationAdded}
            twinId={twinId}
          />
        </>
      )
    }
  }

  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      spacing={2}
      style={{
        paddingLeft: isMobile ? '16px' : 0,
      }}>
      <ConversationsSidebar
        conversationId={conversationId}
        setConversationId={setConversationId}
        conversationsWithTwin={conversationsWithTwin}
        onConversationAdded={onConversationAdded}
        twinId={twinId}
      />
      <Messages
        contextSources={contextSources}
        setContextSources={setContextSources}
        verboseOutput={verboseOutput}
        setVerboseOutput={setVerboseOutput}
        conversation={conversation}
        conversationMessages={conversationMessages}
        isLoadingConversationMessages={isLoadingConversationMessages}
        setConversationId={setConversationId}
        hydrateConversationMessages={hydrateConversationMessages}
        hydrateConversationMessage={hydrateConversationMessage}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
      />
    </Stack>
  )
}

export default TwinProfileContainer
